<template>
  <div
    class="attachment"
    name="timer-modal"
    :class="modalClasses"
    :height="'auto'"
    :width="'300px'"
  >
    <template v-if="timeExpired">
      <pie class="expired" />
      <div class="title">
        Sorry, you ran out of time!
      </div>
      <div
        class="review"
        @click="afterExpiration()"
      >
        Review Explanation
      </div>
    </template>
    <template v-else-if="firstTime">
      <pie />

      <div
        v-if="flashcard.aseShowQuestion"
        class="text"
      >
        <div class="title">
          Do NOT click the green "I'm Ready!" button until you have fully reviewed the case study.
        </div>
        You are free to take all the time you need to review the content of the question and the Composite Vehicle Reference Document.
        <br><br>
        Once you are confident you are ready to see your answer choices, click the green "I'm Ready!" button. The four answer choices will then become visible, and the 5-minute timer will begin counting down.
      </div>
      <div v-else>
        <div class="title">
          Your time is limited
        </div>
        Answer the question before time runs out to earn a credit towards recertification.
      </div>
      <div class="actions">
        <div
          class="back"
          @click="back()"
        >
          Go Back
        </div>
        <div
          class="ready"
          @click="startCountdown()"
        >
          I'm Ready!
        </div>
      </div>
    </template>
    <template v-else>
      <div class="countdown">
        {{ countdown }}
      </div>
      <div class="title ready">
        Ready, Set, GO!
      </div>
      <div
        class="not-ready"
        @click="back()"
      >
        I'm not ready, go back
      </div>
    </template>
  </div>
</template>

<script>
import Pie from '@components/singletons/pie';
import GlobalMixin from '@/mixins/global';

export default {
  components: { Pie },
  mixins: [GlobalMixin],
  props: {
    isFirstTime: {
      type: Boolean,
      default: false,
    },
    flashcard: {
      type: Object,
      default: {},
    },
  },
  emits: ['beforeTimerModalOpen', 'expiredModalClosed', 'timerStart'],
  data() {
    return {
      countdown: 3,
      timeExpired: false,
      timer: null,
      firstTime: false,
    };
  },
  computed: {
    modalClasses() {
      if (this.firstTime || this.timeExpired) {
        return ['timer-modal'];
      } else {
        return ['timer-modal', 'countdown-modal'];
      }
    },
  },
  beforeUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  },
  created() {
    this.firstTime = this.isFirstTime;
  },
  mounted() {
    this.$emit('beforeTimerModalOpen');
    if (!this.firstTime && !this.timeExpired) {
      this.startCountdown();
    }
  },
  methods: {
    afterExpiration() {
      this.$emit('expiredModalClosed');
      this.timeExpired = false;
    },
    back() {
      clearInterval(this.timer);
      this.$router.push(this.currentResource.route);
    },
    start() {
      this.$eventBus.$emit('timer-start');
      this.$emit('timerStart');
    },
    startCountdown() {
      this.firstTime = false;
      this.countdown = 3;
      const context = this;
      this.timer = setInterval(function() {
        if(context.countdown > 0) {
          --context.countdown;
        } else {
          clearInterval(context.timer);
          context.start();
        }
      }, 1000);
    },
  },
};
</script>

<style lang='scss'>
  .timer-modal {
    background: white;
    border-radius: 4px;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: rem-calc(32);
    text-align: center;

    &.countdown-modal {
      background: #1669BA;
    }

    svg {
      margin: 0 auto;

      &.expired circle {
        stroke: #fd7e78 !important;
      }

      @include breakpoint(small) {
        height: rem-calc(64);
        width: rem-calc(64);
      }
    }

    .title {
      color: #12263b;
      font-size: rem-calc(20);
      font-weight: $bold;
      margin: rem-calc(20) 0;
      text-align: center;

      &.ready {
        color: white;
        font-size: rem-calc(26);
        font-weight: $semi-bold;
      }

      @include breakpoint(small) {
        font-size: rem-calc(24);
        margin-top: rem-calc(18);
      }
    }

    .text {
      color: #747E89;
      font-size: rem-calc(16);
      text-align: center;
    }

    .actions {
      display: flex;
      flex-direction: row;
      justify-content: center;
      margin-top: rem-calc(20);
      text-align: center;

      .back, .ready {
        border-radius: 4px;
        color: white;
        cursor: pointer;
        font-size: rem-calc(14);
        font-weight: $semi-bold;
        padding: rem-calc(8) rem-calc(20);
        width: rem-calc(72);


        @include breakpoint(large) {
          font-size: rem-calc(10);
        }

        @include breakpoint(small) {
          font-size: rem-calc(16);
          width: rem-calc(88);
        }
      }

      .back {
        background: red;
        margin-right: rem-calc(4);
      }

      .ready {
        background: #7bd56e;
        margin-left: rem-calc(4);
      }
    }

    .countdown {
      align-items: center;
      background: #08314f;
      border-radius: 5px;
      color: white;
      display: flex;
      font-size: rem-calc(42);
      font-weight: $bold;
      height: rem-calc(100);
      justify-content: center;
      margin: 0 auto;
      width: rem-calc(100);
    }

    .not-ready {
      background: #2977C1;
      border: 1px solid white;
      border-radius: 4px;
      color: white;
      cursor: pointer;
      font-size: rem-calc(14);
      font-weight: $semi-bold;
      padding: rem-calc(5);
    }

    .review {
      background: #7cdb6e;
      border-radius: 4px;
      color: white;
      cursor: pointer;
      font-size: rem-calc(14);
      font-weight: $semi-bold;
      margin: 0 rem-calc(16);
      padding: rem-calc(8);
    }
  }

  .v--modal-box {
    top: unset !important;
    left: unset !important;
    width: unset !important;
    height: unset !important;
    position: unset;
    overflow: unset;
    box-sizing: unset;
  }
  .v--modal-overlay[data-modal="timer-modal"] {
    background: rgba(0, 0, 0, 0.1);
    position: absolute;
    height: 100%;

    @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
      background: #1F74D6;
    }

    .timer-modal {
      // left: calc(50% - 150px) !important;
      // top: calc(50% - 135px) !important;
    }
  }
</style>
